import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { mockUser, mockStats, mockBadges, mockHeatmap } from '@/mocks/userData';

// 蓝紫色系热力图四级色
const heatLevels = [
  'rgba(181,198,224,0.18)',
  'rgba(181,198,224,0.42)',
  'rgba(168,184,216,0.68)',
  'rgba(148,168,208,0.90)',
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null);

  const statCards = [
    { label: '累计专注', value: mockStats.totalFocusHours, unit: '小时' },
    { label: '累计打卡', value: mockStats.totalCheckIns, unit: '次' },
    { label: '坚持天数', value: mockStats.streakDays, unit: '天' },
  ];

  const menuItems = [
    { icon: 'ri-moon-line', label: '主题切换' },
    { icon: 'ri-timer-2-line', label: '番茄钟设置' },
    { icon: 'ri-notification-3-line', label: '习惯提醒' },
    { icon: 'ri-shield-line', label: '隐私协议' },
    { icon: 'ri-information-line', label: '关于我们' },
  ];

  const weekFocusHours = Math.round(mockStats.totalFocusHours * 0.15);

  return (
    <div className="h-full flex flex-col relative" style={{ background: '#F5F7FA', fontFamily: '\'Noto Sans SC\', sans-serif' }}>
      <div className="flex-1 overflow-y-auto relative" style={{ scrollbarWidth: 'none' }}>
        <div className="absolute top-0 left-0 right-0 h-56 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(181,198,224,0.22) 0%, transparent 80%)' }} />

        {/* User info */}
        <div className="px-4 pt-12 pb-5 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' }}>
            <i className="ri-user-smile-line text-3xl text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-base" style={{ color: '#2D2B3D' }}>{mockUser.nickname}</span>
              <button className="w-5 h-5 flex items-center justify-center cursor-pointer">
                <i className="ri-edit-line text-sm" style={{ color: '#C5B9D8' }} />
              </button>
            </div>
            <p className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>{mockUser.signature}</p>
          </div>
        </div>

        {/* Stats — 蓝紫色数字 */}
        <div className="px-4 mb-5 flex gap-3">
          {statCards.map(card => (
            <div key={card.label} className="flex-1 rounded-2xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(12px)', border: '1px solid rgba(181,198,224,0.2)' }}>
              <div className="font-bold tabular-nums" style={{ fontSize: 24, color: '#A8B8D8' }}>{card.value}</div>
              <div className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>{card.unit}</div>
              <div className="text-xs mt-0.5" style={{ color: '#B0B0B0' }}>{card.label}</div>
            </div>
          ))}
        </div>

        {/* 热力图 — 蓝紫色系 */}
        <div className="px-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-sm" style={{ color: '#2D2B3D' }}>本月足迹</span>
            <div className="flex items-center gap-2">
              <button className="w-6 h-6 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line" style={{ color: '#C5B9D8' }} /></button>
              <span className="text-xs" style={{ color: '#8E8E93' }}>4月</span>
              <button className="w-6 h-6 flex items-center justify-center cursor-pointer"><i className="ri-arrow-right-s-line" style={{ color: '#C5B9D8' }} /></button>
            </div>
          </div>
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(12px)', border: '1px solid rgba(181,198,224,0.2)' }}>
            <div className="flex flex-wrap gap-1.5">
              {mockHeatmap.map((d, i) => (
                <div key={i} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: heatLevels[d.level] }}>
                  <span style={{ fontSize: 9, color: d.level > 1 ? '#8B9DC3' : '#B0B0B0' }}>{d.day}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs" style={{ color: '#B0B0B0' }}>低</span>
              {heatLevels.map((c, i) => <div key={i} className="w-3 h-3 rounded-sm" style={{ background: c }} />)}
              <span className="text-xs" style={{ color: '#B0B0B0' }}>高</span>
            </div>
          </div>
        </div>

        {/* 专注明信片入口 */}
        <div className="px-4 mb-4">
          <button
            onClick={() => navigate('/postcard')}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl cursor-pointer active:scale-95 transition-transform"
            style={{
              background: 'rgba(197,185,216,0.15)',
              border: '1px solid rgba(181,198,224,0.25)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' }}>
              <i className="ri-mail-send-line text-white text-base" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold" style={{ color: '#A8B8D8' }}>本周专注明信片</p>
              <p className="text-xs mt-0.5" style={{ color: '#C5B9D8' }}>本周累计专注 {weekFocusHours} 小时</p>
            </div>
            <i className="ri-arrow-right-s-line" style={{ color: '#C5B9D8' }} />
          </button>
        </div>

        {/* Badges */}
        <div className="px-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-sm" style={{ color: '#2D2B3D' }}>我的徽章</span>
            <span className="text-xs" style={{ color: '#8E8E93' }}>共{mockBadges.length}枚</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {mockBadges.map(badge => (
              <button key={badge.id} onClick={() => setSelectedBadge(badge.id)} className="flex flex-col items-center gap-1.5 flex-shrink-0 cursor-pointer active:scale-90 transition-transform">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: badge.earned ? 'rgba(181,198,224,0.28)' : 'rgba(200,200,200,0.15)', border: badge.earned ? '1.5px solid rgba(181,198,224,0.45)' : '1.5px solid rgba(200,200,200,0.2)' }}>
                  <i className={`${badge.icon} text-xl`} style={{ color: badge.earned ? '#A8B8D8' : '#C0C0C0' }} />
                </div>
                <span style={{ fontSize: 10, color: badge.earned ? '#A8B8D8' : '#C0C0C0', whiteSpace: 'nowrap' }}>{badge.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 成长记录相册入口 */}
        <div className="px-4 mb-4">
          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(12px)', border: '1px solid rgba(181,198,224,0.2)' }}>
            <button
              onClick={() => navigate('/growth')}
              className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors"
            >
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-image-line text-base" style={{ color: '#C5B9D8' }} />
              </div>
              <span className="flex-1 text-sm text-left" style={{ color: '#2D2B3D' }}>成长记录相册</span>
              <i className="ri-arrow-right-s-line" style={{ color: '#D0D0D0' }} />
            </button>
          </div>
        </div>

        {/* Menu */}
        <div className="px-4 mb-4">
          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(12px)', border: '1px solid rgba(181,198,224,0.2)' }}>
            {menuItems.map((item, i) => (
              <div key={item.label}>
                <button className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} style={{ color: '#C5B9D8' }} />
                  </div>
                  <span className="flex-1 text-sm text-left" style={{ color: '#2D2B3D' }}>{item.label}</span>
                  <i className="ri-arrow-right-s-line" style={{ color: '#D0D0D0' }} />
                </button>
                {i < menuItems.length - 1 && <div className="mx-4 h-px" style={{ background: 'rgba(181,198,224,0.15)' }} />}
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs pb-6" style={{ color: '#C0C0C0' }}>轻醒 v1.0.0</p>
      </div>

      <BottomNav />

      {/* Badge modal */}
      {selectedBadge && (() => {
        const badge = mockBadges.find(b => b.id === selectedBadge);
        if (!badge) return null;
        return (
          <div className="absolute inset-0 z-50 flex items-center justify-center" onClick={() => setSelectedBadge(null)}>
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative rounded-3xl p-8 flex flex-col items-center gap-3 mx-8" style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(16px)' }} onClick={e => e.stopPropagation()}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: badge.earned ? 'rgba(181,198,224,0.3)' : 'rgba(200,200,200,0.2)' }}>
                <i className={`${badge.icon} text-4xl`} style={{ color: badge.earned ? '#A8B8D8' : '#C0C0C0' }} />
              </div>
              <p className="font-semibold text-base" style={{ color: '#2D2B3D' }}>{badge.name}</p>
              <p className="text-sm text-center" style={{ color: '#8E8E93' }}>{badge.earned ? '已获得此徽章，继续保持！' : '坚持打卡即可解锁此徽章'}</p>
              <button onClick={() => setSelectedBadge(null)} className="mt-2 px-6 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)', color: '#fff' }}>知道了</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
